import React, { useState, useEffect, useCallback } from 'react';
import SidebarLayout from './SidebarLayout';
import axios from 'axios';

function Waitlist() {
  const [waitlist, setWaitlist] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [manualWaitlistActionsEnabled, setManualWaitlistActionsEnabled] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    serviceId: '',
    staffId: ''
  });

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.ogestyle.com';
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('ogestyle_token');
    if (!token) {
      window.location.href = '/login';
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchData = useCallback(async () => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      // PHASE 4 FIX: Use my-shops endpoint instead of User.shopIds
      const shopsResponse = await axios.get(`${API_BASE_URL}/api/shop/my-shops`, { headers });
      const shops = shopsResponse.data?.shops || shopsResponse.data || [];
      
      if (!shops.length) {
        alert('No shop found for your account');
        return;
      }
      
      const shopId = shops[0]._id;

      // Build query params
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.serviceId) params.append('serviceId', filters.serviceId);
      if (filters.staffId) params.append('staffId', filters.staffId);

      // Fetch waitlist
      const waitlistResponse = await axios.get(
        `${API_BASE_URL}/api/waitlist/shop/${shopId}?${params.toString()}`,
        { headers }
      );
      setWaitlist(waitlistResponse.data.waitlist || []);
      setManualWaitlistActionsEnabled(waitlistResponse.data.manualWaitlistActionsEnabled === true);

      // Fetch services
      const servicesResponse = await axios.get(`${API_BASE_URL}/api/services/shop/${shopId}`, { headers });
      setServices(servicesResponse.data.services || []);

      // Fetch staff - use business-wide staff list instead of shop-specific
      const shopData = shops[0];
      const businessId = shopData.businessId?._id || shopData.businessId;
      
      if (businessId) {
        const staffResponse = await axios.get(
          `${API_BASE_URL}/api/staff/list?businessId=${businessId}`,
          { headers }
        );
        // Filter to only show R3 professionals (not owners or admins)
        const professionalStaff = (staffResponse.data.staff || []).filter(s => 
          s.status === 'Active' && s.role === 'R3'
        );
        setStaff(professionalStaff);
      } else {
        setStaff([]);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('ogestyle_token');
        window.location.href = '/login';
      }
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, getAuthHeaders, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleNotify = async (waitlistId) => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      await axios.post(`${API_BASE_URL}/api/waitlist/${waitlistId}/notify`, {}, { headers });
      alert('Customer notified of availability!');
      fetchData();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to notify customer';
      alert(message);
    }
  };

  const handleUpdatePriority = async (waitlistId, priority) => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      await axios.patch(`${API_BASE_URL}/api/waitlist/${waitlistId}/priority`, { priority }, { headers });
      alert('Priority updated!');
      fetchData();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update priority';
      alert(message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const rawValue = String(dateString);
    const dateOnlyMatch = rawValue.match(/^(\d{4}-\d{2}-\d{2})/);
    const date = dateOnlyMatch
      ? new Date(`${dateOnlyMatch[1]}T12:00:00`)
      : new Date(dateString);

    if (Number.isNaN(date.getTime())) {
      return 'N/A';
    }

    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatStatus = (status) => {
    const labels = {
      pending: 'Pending',
      active: 'Active',
      notified: 'Notified',
      booked: 'Booked',
      closed_booked_occurred: 'Closed (Booked Occurred)',
      closed_cancelled: 'Closed (Cancelled)',
      closed_fallback_mismatch: 'Closed (Fallback Mismatch)',
      expired: 'Expired',
      cancelled: 'Cancelled'
    };
    return labels[status] || String(status || 'Unknown');
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#7c3aed',
      active: '#10b981',
      notified: '#3b82f6',
      booked: '#8b5cf6',
      closed_booked_occurred: '#7c3aed',
      closed_cancelled: '#b91c1c',
      closed_fallback_mismatch: '#d97706',
      expired: '#6b7280',
      cancelled: '#ef4444'
    };
    return colors[status] || '#6b7280';
  };

  if (loading) {
    return (
      <SidebarLayout>
        <div className="waitlist-page" style={{ padding: '40px', textAlign: 'center' }}>
          <div style={{ fontSize: '24px', marginBottom: '16px' }}>Loading waitlist...</div>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="waitlist-page" style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '32px', fontWeight: '700', color: '#1a1a1a', margin: 0 }}>
            Waitlist Management
          </h1>
          <p style={{ color: '#666', marginTop: '8px' }}>
            {manualWaitlistActionsEnabled
              ? 'Manage customer waitlist with manual actions enabled'
              : 'Monitor pending and active customer waitlist requests (automated notifications enabled)'}
          </p>
        </div>

        {/* Filters */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px'
          }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  fontSize: '14px'
                }}
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="active">Active</option>
                <option value="notified">Notified</option>
                <option value="booked">Booked</option>
                <option value="closed_booked_occurred">Closed (Booked Occurred)</option>
                <option value="closed_cancelled">Closed (Cancelled)</option>
                <option value="expired">Expired</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                Service
              </label>
              <select
                value={filters.serviceId}
                onChange={(e) => setFilters(prev => ({ ...prev, serviceId: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  fontSize: '14px'
                }}
              >
                <option value="">All Services</option>
                {services.map(s => (
                  <option key={s._id} value={s._id}>{s.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                Professional
              </label>
              <select
                value={filters.staffId}
                onChange={(e) => setFilters(prev => ({ ...prev, staffId: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  fontSize: '14px'
                }}
              >
                <option value="">All Professionals</option>
                {staff.map(s => (
                  <option key={s._id} value={s._id}>
                    {s.firstName && s.lastName 
                      ? `${s.firstName} ${s.lastName}` 
                      : (s.email || 'Unknown')}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Waitlist Table */}
        {waitlist.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            background: '#f9fafb',
            borderRadius: '12px',
            border: '2px dashed #e5e7eb'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
              No waitlist entries
            </h3>
            <p style={{ color: '#6b7280' }}>
              {filters.status || filters.serviceId || filters.staffId 
                ? 'Try adjusting your filters'
                : 'Customers will appear here when they join the waitlist'}
            </p>
          </div>
        ) : (
          <div style={{ 
            background: 'white', 
            borderRadius: '12px', 
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', fontSize: '14px', color: '#374151' }}>
                      Customer
                    </th>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', fontSize: '14px', color: '#374151' }}>
                      Service
                    </th>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', fontSize: '14px', color: '#374151' }}>
                      Professional
                    </th>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', fontSize: '14px', color: '#374151' }}>
                      Preferred Dates
                    </th>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', fontSize: '14px', color: '#374151' }}>
                      Status
                    </th>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', fontSize: '14px', color: '#374151' }}>
                      Priority
                    </th>
                    {manualWaitlistActionsEnabled && (
                      <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600', fontSize: '14px', color: '#374151' }}>
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {waitlist.map((entry) => (
                    <tr key={entry._id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '16px' }}>
                        <div style={{ fontWeight: '600', color: '#1a1a1a', marginBottom: '4px' }}>
                          {entry.customerName || entry.customerId?.name || 'N/A'}
                        </div>
                        <div style={{ fontSize: '13px', color: '#6b7280' }}>
                          {entry.customerEmail}
                        </div>
                        <div style={{ fontSize: '13px', color: '#6b7280' }}>
                          {entry.customerPhone}
                        </div>
                      </td>
                      <td style={{ padding: '16px', fontWeight: '500', color: '#374151' }}>
                        {entry.serviceId?.name || 'N/A'}
                      </td>
                      <td style={{ padding: '16px', color: '#374151' }}>
                        {entry.staffId?.name || 'N/A'}
                      </td>
                      <td style={{ padding: '16px' }}>
                        {entry.preferredDates && entry.preferredDates.length > 0 ? (
                          <div style={{ fontSize: '13px', color: '#374151' }}>
                            {entry.preferredDates.slice(0, 2).map((date, i) => (
                              <div key={i}>{formatDate(date)}</div>
                            ))}
                            {entry.preferredDates.length > 2 && (
                              <div style={{ color: '#6b7280' }}>+{entry.preferredDates.length - 2} more</div>
                            )}
                          </div>
                        ) : entry.flexibleDates ? (
                          <span style={{ fontSize: '13px', color: '#6b7280' }}>Flexible</span>
                        ) : (
                          <span style={{ fontSize: '13px', color: '#6b7280' }}>None specified</span>
                        )}
                      </td>
                      <td style={{ padding: '16px' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '13px',
                          fontWeight: '600',
                          background: `${getStatusColor(entry.status)}20`,
                          color: getStatusColor(entry.status)
                        }}>
                          {formatStatus(entry.status)}
                        </span>
                      </td>
                      <td style={{ padding: '16px' }}>
                        {manualWaitlistActionsEnabled ? (
                          <select
                            value={entry.priority || 0}
                            onChange={(e) => handleUpdatePriority(entry._id, parseInt(e.target.value))}
                            style={{
                              padding: '6px 10px',
                              borderRadius: '6px',
                              border: '2px solid #e5e7eb',
                              fontSize: '13px',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            <option value="0">Normal</option>
                            <option value="1">High</option>
                            <option value="2">Urgent</option>
                          </select>
                        ) : (
                          <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: '600' }}>
                            {entry.priority === 2
                              ? 'Urgent'
                              : entry.priority === 1
                                ? 'High'
                                : 'Normal'}
                          </span>
                        )}
                      </td>
                      {manualWaitlistActionsEnabled && (
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          {entry.status === 'active' && (
                            <button
                              onClick={() => handleNotify(entry._id)}
                              style={{
                                padding: '8px 16px',
                                background: '#3b82f6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: '600'
                              }}
                            >
                              Notify
                            </button>
                          )}
                          {entry.status === 'notified' && (
                            <span style={{ fontSize: '13px', color: '#6b7280' }}>
                              Notified {formatDate(entry.lastNotificationAt)}
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </SidebarLayout>
  );
}

export default Waitlist;
